import { createContext, useContext } from 'react';

/**
 * A one-shot request to open Lumio and ask it something (e.g. the product
 * detail page's "Tell me more about {{name}}" pill). `nonce` makes each request
 * distinct, so the same prompt can be re-asked and so the active Lumio surface
 * sends a given seed exactly once. The consuming surface clears it via
 * `consumeSeed`.
 */
export interface LumioSeed {
  /** The (already-localized) prompt to send as the first user turn. */
  text: string;
  /** Bumped per request; distinguishes repeat asks of the same prompt. */
  nonce: number;
}

export interface LumioControl {
  /**
   * Open Lumio in whichever surface fits the viewport (docked column on wide
   * desktop, slide-in drawer on mid-desktop, full-screen overlay on mobile) and
   * send `prompt` as the first user turn. No-op for an empty/whitespace prompt.
   */
  askLumio: (prompt: string) => void;
  /** Pending seed for the active Lumio surface to send; `null` when none / consumed. */
  seed: LumioSeed | null;
  /**
   * Highest seed nonce already sent by some surface. A surface only sends a seed
   * whose nonce exceeds this, so a given prompt is sent at most once across ALL
   * surfaces and remounts — even if the surface that should have sent it
   * unmounted (breakpoint switch) before its effect ran, leaving a per-instance
   * guard reset. The shared marker, not a per-instance ref, is what makes the
   * at-most-once invariant hold.
   */
  consumedNonce: number;
  /** Mark `nonce` sent (records it as `consumedNonce` and clears the pending seed). */
  consumeSeed: (nonce: number) => void;
  /**
   * A support agent is signed in AS this owner (MainLayout's impersonation): the Lumio surfaces keep the FTUX tests
   * off, refusing the "777ftuxqa" code and showing no QA strip. Read here so the chat never imports the
   * store.
   */
  impersonating?: boolean;
}

const noop = (): void => undefined;

/**
 * Lumio controller, provided by `MainLayout` and consumed by both the page tree
 * (to ask) and the Lumio surfaces (to send). The default is inert so the Lumio
 * components still mount standalone in their Playwright CTs with no provider.
 */
export const LumioControlContext = createContext<LumioControl>({
  askLumio: noop,
  seed: null,
  consumedNonce: 0,
  consumeSeed: noop,
  impersonating: false,
});

/** Access the Lumio controller (open Lumio + seed a prompt) from under `MainLayout`. */
export const useLumioControl = (): LumioControl => useContext(LumioControlContext);
